/*
 * Mono-Shift-aware Pixel-Geometrie fuer proportionalen Text mit Inline-Mono-
 * Spans (Inline-Code im Editor, CONSOLE_AI_CODE in der Console).
 *
 * Spiegelt die Glyph-Layout-Logik des Renderers, sodass Wrap-Width und
 * Selection-Geometrie konsistent sind. Lesson 82 (2026-05-02): Vor der
 * Extraktion lebte die Logik dupliziert in renderer.js#renderProportionalLine
 * (Selection-Pre-Pass) und core/CoreView.js#_buildEffectivePositions (Wrap-
 * Berechnung) — drei Bugs in derselben Iteration zeigten, dass die Logik
 * fehleranfaellig ist und auseinanderdriften kann.
 *
 * Ein Inline-Mono-Run bricht die proportionale Glyph-Position: jedes Zeichen
 * im Run sitzt auf einem festen Mono-Grid (monoCharWidth), Folgezeichen nach
 * dem Run werden um die Differenz "Mono-Endposition − proportionale
 * Endposition" verschoben (`monoShift`).
 *
 * Verwendung im Renderer: `useMono = formatRuns[i].mono || styles[style].isMono`.
 * Das `mono`-Flag am FormatRun ist die F1-Schiene (Inline-Code als 5. Format
 * im Composite-Renderer), die ohne Style-Wechsel auskommen muss.
 */

/**
 * @param {{positions: Float32Array|number[], totalWidth: number}} layout
 *        Wrap-Layout aus computeStringLayout
 * @param {Array<{start:number, end:number, style?:string, mono?:boolean}>|null} formatRuns
 *        Format-Runs in displayText-Koordinaten, sortiert und nicht-ueberlappend
 * @param {Object<string, {isMono?:boolean}>} stylesTable
 *        STYLES aus config.js — fuer style-basierte Mono-Erkennung
 * @param {number} scaleFactor
 *        Skalierung, wie der Renderer sie benutzt (heightQ-abhaengig)
 * @param {number} monoCharWidth
 *        Mono-Glyph-Breite bei der aktuellen heightQ
 * @param {number} cpLen
 *        Anzahl Codepoints im displayText
 * @param {number} [startX=0]
 *        Optionaler Pixel-Offset, wenn der Aufrufer absolute Koordinaten will
 * @returns {Float32Array}
 *        effLeft[i] = linke Pixel-Kante Codepoint i (mit Offset),
 *        effLeft[cpLen] = rechtes Ende
 */
export function buildMonoShiftedPositions(
    layout, formatRuns, stylesTable, scaleFactor, monoCharWidth, cpLen, startX = 0
) {
    const effLeft = new Float32Array(cpLen + 1)
    if (cpLen === 0) {
        effLeft[0] = startX
        return effLeft
    }

    const runCount = formatRuns ? formatRuns.length : 0
    let runIdx = 0
    let monoRunStart = -1
    let monoRunPixelX = 0
    let monoShift = 0

    for (let i = 0; i < cpLen; i++) {
        while (runIdx < runCount && i >= formatRuns[runIdx].end) runIdx++
        const inRun = runIdx < runCount && i >= formatRuns[runIdx].start
        const styleName = inRun ? formatRuns[runIdx].style : null
        // Compound-Suffix (z.B. EDITOR_CODE+BG) auf Base-Namen reduzieren —
        // STYLES enthaelt nur den Base-Eintrag mit isMono. Ohne diesen Split
        // schlaegt der Lookup fuer Background-Separatoren fehl, der Glyph wird
        // dann mono gerendert (Renderer geht ueber _resolveStyle, das splittet),
        // aber die Geometrie bleibt proportional → End-Marker und Selection-
        // Kante sitzen am proportionalen Ende statt am mono-shifted Ende.
        const styleBase = styleName && styleName.includes('+') ? styleName.split('+')[0] : styleName
        const styleMono = !!(styleBase && stylesTable[styleBase] && stylesTable[styleBase].isMono)
        const runMono = inRun && !!formatRuns[runIdx].mono
        const useMono = styleMono || runMono

        if (useMono && monoRunStart < 0) {
            monoRunStart = i
            monoRunPixelX = startX + layout.positions[i] * scaleFactor + monoShift
        } else if (!useMono && monoRunStart >= 0) {
            const monoEndX = monoRunPixelX + (i - monoRunStart) * monoCharWidth
            const propEndX = startX + layout.positions[i] * scaleFactor + monoShift
            monoShift += (monoEndX - propEndX)
            monoRunStart = -1
        }

        if (useMono) {
            effLeft[i] = monoRunPixelX + (i - monoRunStart) * monoCharWidth
        } else {
            effLeft[i] = startX + layout.positions[i] * scaleFactor + monoShift
        }
    }

    if (monoRunStart >= 0) {
        effLeft[cpLen] = monoRunPixelX + (cpLen - monoRunStart) * monoCharWidth
    } else {
        effLeft[cpLen] = startX + layout.totalWidth * scaleFactor + monoShift
    }

    return effLeft
}
